import {
  autocompletion,
  type Completion,
  type CompletionContext,
  type CompletionResult,
  pickedCompletion,
} from '@codemirror/autocomplete';
import type { EditorView } from '@codemirror/view';

import { FilterId } from '../../../filters/types/filters';
import {
  type MetricValues,
  metricOptions,
} from '../../PageConfigProvider/types/metric';
import { getHelperDefinition, HELPER_REGISTRY } from './helper-registry';
import { buildHelperInfoDom } from './tooltip-extension';
import { normalizeDataset } from './utils/extractDatasetValues';
import type { DataContext } from './utils/handlebars-setup';

const FORMAT_OPTIONS = ['SI', 'PERCENTAGE', 'DATE', 'ORDINAL', '$'] as const;
const REDUCE_OPTIONS = [
  'first',
  'last',
  'min',
  'max',
  'sum',
  'avg',
  'median',
  'time_weighted_avg',
] as const;
const HELPER_TRIGGER_RE = /(?:\{\{\s*|\(\s*)([A-Za-z_]*)$/;
const METRIC_CONTEXT_RE = /(?:\{\{|\()\s*([A-Za-z_][A-Za-z0-9_]*)\s+"([^"]*)$/;
const ENTITY_CONTEXT_RE = /entity\s*=\s*"([^"]*)$/i;
const FORMAT_CONTEXT_RE = /format\s*=\s*"([^"]*)$/i;
const REDUCE_CONTEXT_RE = /reduce\s*=\s*"([^"]*)$/i;
const FILTER_ID_CONTEXT_RE = /^\{\{\s*(FILTER|PARAM|HAS_PARAM)\s+"([^"]*)$/i;
const HASH_KEY_CONTEXT_RE = /(?:\s|^)([A-Za-z_]*)$/;

const HELPER_HASH_PARAMS: Record<string, string[]> = {
  VALUE: [
    'entity=',
    'segment=',
    'label=',
    'date=',
    'percentile=',
    'field=',
    'whereField=',
    'equals=',
    'labelField=',
    'timeAggregation=',
    'excludeEntity=',
    'reduce=',
  ],
  TABLE: [
    'reduce=',
    'format=',
    'limit=',
    'entity=',
    'segment=',
    'label=',
    'date=',
    'percentile=',
    'field=',
    'whereField=',
    'equals=',
    'labelField=',
    'timeAggregation=',
    'excludeEntity=',
  ],
  ARG_MAX: ['reduce=', 'segment=', 'field=', 'labelField=', 'entity='],
  ARG_MIN: ['reduce=', 'segment=', 'field=', 'labelField=', 'entity='],
  RANK: ['entity=', 'reduce=', 'segment=', 'field=', 'labelField='],
  SHARE: ['entity=', 'reduce=', 'segment=', 'field=', 'labelField='],
  MAX_POINT: ['entity=', 'property=', 'segment='],
  MIN_POINT: ['entity=', 'property=', 'segment='],
  PERCENTILE_RANK: ['entity=', 'reduce=', 'segment=', 'field=', 'labelField='],
  FORMAT: ['format='],
};

function getOpenMustacheBlock(prefix: string): string | null {
  const lastOpen = prefix.lastIndexOf('{{');
  if (lastOpen < 0) return null;
  const lastClose = prefix.lastIndexOf('}}');
  if (lastClose > lastOpen) return null;
  return prefix.slice(lastOpen);
}

function getHelperNameFromBlock(block: string): string | null {
  const match = block.match(/^\{\{\s*([A-Za-z_][A-Za-z0-9_]*)/);
  return match?.[1] ?? null;
}

function getMetricArgFromBlock(block: string): string | null {
  const match = block.match(/^\{\{\s*[A-Za-z_][A-Za-z0-9_]*\s+"([^"]*)"/);
  return match?.[1] ?? null;
}

function createHelperCompletions(typed: string): Completion[] {
  return Object.values(HELPER_REGISTRY)
    .filter((helper) =>
      helper.name.toLowerCase().startsWith(typed.toLowerCase())
    )
    .map((helper) => ({
      label: helper.name,
      type: 'keyword',
      detail: helper.description,
      info: () => buildHelperInfoDom(helper),
      apply: helper.signature.includes(' ') ? `${helper.name} ` : helper.name,
    }));
}

function applyWithOptionalCloseQuote(
  view: EditorView,
  completion: Completion,
  from: number,
  to: number
): void {
  const next = view.state.doc.sliceString(to, to + 1);
  const text = next === '"' ? completion.label : `${completion.label}"`;
  view.dispatch({
    changes: { from, to, insert: text },
    annotations: [pickedCompletion.of(completion)],
  });
}

function createValueCompletions(
  values: string[],
  typed: string,
  type: Completion['type'] = 'variable',
  options?: { closeQuote: boolean }
): Completion[] {
  return values
    .filter((value) => value.toLowerCase().startsWith(typed.toLowerCase()))
    .map((value) => ({
      label: value,
      type,
      ...(options?.closeQuote === true
        ? { apply: applyWithOptionalCloseQuote }
        : undefined),
    }));
}

function extractEntitySuggestions(dataContext: DataContext): string[] {
  const entities = new Set<string>();
  const multiData = dataContext.multiData;
  if (!multiData) return [];

  (Object.keys(multiData) as MetricValues[]).forEach((metric) => {
    const dataset = multiData[metric];
    if (!dataset) return;
    const normalized = normalizeDataset(dataset);
    normalized.forEach((point) => {
      if (point.entity) entities.add(point.entity);
    });
  });

  return Array.from(entities).sort((a, b) => a.localeCompare(b));
}

function extractEntitySuggestionsForMetric(
  dataContext: DataContext,
  metric: string | null
): string[] {
  const multiData = dataContext.multiData;
  if (!multiData) return [];

  const companyDataset = multiData.headcount;
  if (companyDataset) {
    const companyEntities = new Set<string>();
    normalizeDataset(companyDataset).forEach((point) => {
      if (point.entity) companyEntities.add(point.entity);
    });
    if (companyEntities.size > 0) {
      return Array.from(companyEntities).sort((a, b) => a.localeCompare(b));
    }
  }

  if (!metric) return extractEntitySuggestions(dataContext);
  const dataset = multiData[metric as MetricValues];
  if (!dataset) return extractEntitySuggestions(dataContext);
  const entities = new Set<string>();
  normalizeDataset(dataset).forEach((point) => {
    if (point.entity) entities.add(point.entity);
  });
  return Array.from(entities).sort((a, b) => a.localeCompare(b));
}

function getMetricCompletions(
  dataContext: DataContext,
  typed: string,
  options?: { closeQuote: boolean }
): Completion[] {
  const allMetricKeys = metricOptions.map(
    (option: { value: string }) => option.value
  );
  const loadedMetrics = new Set(Object.keys(dataContext.multiData ?? {}));
  return createValueCompletions(allMetricKeys, typed, 'variable', options).sort(
    (a, b) => {
      const aLoaded = loadedMetrics.has(a.label);
      const bLoaded = loadedMetrics.has(b.label);
      if (aLoaded !== bLoaded) return aLoaded ? -1 : 1;
      return a.label.localeCompare(b.label);
    }
  );
}

export function autocompleteExtension(getDataContext: () => DataContext) {
  return autocompletion({
    override: [
      (context: CompletionContext): CompletionResult | null => {
        const dataContext = getDataContext();
        const prefix = context.state.doc.sliceString(0, context.pos);
        const currentBlock = getOpenMustacheBlock(prefix);

        if (currentBlock) {
          const filterIdMatch = currentBlock.match(FILTER_ID_CONTEXT_RE);
          if (filterIdMatch) {
            const typed = filterIdMatch[2] ?? '';
            return {
              from: context.pos - typed.length,
              options: createValueCompletions(
                Object.values(FilterId),
                typed,
                'enum',
                { closeQuote: true }
              ),
              validFor: /^[^"]*$/,
            };
          }
        }

        const entityMatch = prefix.match(ENTITY_CONTEXT_RE);
        if (entityMatch && currentBlock) {
          const typed = entityMatch[1] ?? '';
          const from = context.pos - typed.length;
          const options = createValueCompletions(
            extractEntitySuggestionsForMetric(
              dataContext,
              getMetricArgFromBlock(currentBlock)
            ),
            typed,
            'variable',
            { closeQuote: true }
          );
          return {
            from,
            options,
            validFor: /^[^"]*$/,
          };
        }

        const reduceMatch = prefix.match(REDUCE_CONTEXT_RE);
        if (reduceMatch) {
          const typed = reduceMatch[1] ?? '';
          const from = context.pos - typed.length;
          const options = createValueCompletions(
            [...REDUCE_OPTIONS],
            typed,
            'constant',
            { closeQuote: true }
          );
          return {
            from,
            options,
            validFor: /^[^"]*$/,
          };
        }

        const formatMatch = prefix.match(FORMAT_CONTEXT_RE);
        if (formatMatch) {
          const typed = formatMatch[1] ?? '';
          const from = context.pos - typed.length;
          const options = createValueCompletions(
            [...FORMAT_OPTIONS],
            typed,
            'constant',
            { closeQuote: true }
          );
          return {
            from,
            options,
            validFor: /^[^"]*$/,
          };
        }

        const metricMatch = prefix.match(METRIC_CONTEXT_RE);
        if (metricMatch) {
          const helperName = metricMatch[1] ?? '';
          const typed = metricMatch[2] ?? '';
          const helper = getHelperDefinition(helperName.toLowerCase());
          if (helper?.parameterType === 'metric') {
            return {
              from: context.pos - typed.length,
              options: getMetricCompletions(dataContext, typed, {
                closeQuote: true,
              }),
              validFor: /^[^"]*$/,
            };
          }
        }

        if (currentBlock) {
          const helperName = getHelperNameFromBlock(currentBlock);
          if (helperName) {
            const availableParams =
              HELPER_HASH_PARAMS[helperName.toUpperCase()] ?? [];
            const keyMatch = currentBlock.match(HASH_KEY_CONTEXT_RE);
            const typed = keyMatch?.[1] ?? '';
            if (
              availableParams.length > 0 &&
              !currentBlock.endsWith('"') &&
              !currentBlock.endsWith('=') &&
              !currentBlock.match(/\w+="[^"]*$/)
            ) {
              const options = createValueCompletions(
                availableParams,
                typed,
                'property'
              );
              if (options.length > 0) {
                return {
                  from: context.pos - typed.length,
                  options,
                  validFor: /^[A-Za-z_]*=?$/,
                };
              }
            }
          }
        }

        const helperMatch = prefix.match(HELPER_TRIGGER_RE);
        if (!helperMatch) return null;

        const typed = helperMatch[1] ?? '';
        if (!context.explicit && typed.length === 0) return null;

        return {
          from: context.pos - typed.length,
          options: createHelperCompletions(typed),
          validFor: /^[A-Za-z_]*$/,
        };
      },
    ],
  });
}
